import React, { useRef, useState, useCallback, useEffect } from 'react'

import useUrlsConsent from 'hooks/use-urls-consent'
import { intervalTimes } from 'utils/async'
import useOnResize from 'hooks/use-on-resize'

import Placeholder from './placeholder'

type IframeProps = React.IframeHTMLAttributes<HTMLIFrameElement>

interface Props {
  src: string
  title: string
  includedUrls: string[]
  width?: IframeProps['width']
  initialAspectRatio: number
}

const calcBodyHeight = (body?: HTMLBodyElement) => {
  if (body) {
    // Get the `bottom` position of the body
    const bodyBottom = body.getBoundingClientRect().bottom
    // Get the `bottom` positions of all children
    const elPosBottomList = Array.from(body.children).map(
      (el) => el.getBoundingClientRect().bottom
    )
    const elMaxBottom = Math.max.apply(null, [...elPosBottomList, bodyBottom])
    return Math.ceil(elMaxBottom)
  }
  return undefined
}

const calcIframeHeight = (
  initialAspectRatio: number,
  iframeEl: HTMLIFrameElement | null
) => {
  if (!iframeEl) {
    // Nothing got rendered yet. We can't determine a height
    return ''
  }
  const { width } = iframeEl.getBoundingClientRect()
  const initialHeight = width / initialAspectRatio
  try {
    const iframeDoc = iframeEl?.contentWindow?.document
    const body = iframeDoc?.getElementsByTagName('body')[0]
    const bodyHeight = calcBodyHeight(body)
    return bodyHeight || initialHeight
  } catch {
    // On dev accessing the iframe will throw a CORS SecurityError. This might also happen in
    // other environments, so we're guarding against it using try…catch and return our default
    // height based on the initialAspectRatio.
    return initialHeight
  }
}

const useIframeHeight = (initialAspectRatio: number) => {
  const ref = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState<IframeProps['height']>('')

  const evaluateHeight = useCallback(() => {
    const newHeight = calcIframeHeight(initialAspectRatio, ref.current)
    if (newHeight !== height) {
      setHeight(newHeight)
    }
  }, [initialAspectRatio, ref, height])

  useEffect(evaluateHeight, [ref])

  useOnResize(evaluateHeight, [ref])

  const onLoad = useCallback(async () => {
    evaluateHeight()
    // The page might still change after it fully loaded. Therefore, keep updating the height for
    // 3 more seconds.
    intervalTimes(250, 12, evaluateHeight)
  }, [evaluateHeight])

  return [height, ref, onLoad] as const
}

const ArbitraryCodeEmbed = ({
  src,
  width = '100%',
  initialAspectRatio = 16 / 9,
  title,
  includedUrls,
}: Props) => {
  const areAllowed = useUrlsConsent(includedUrls)
  const [calculatedHeight, ref, onLoad] = useIframeHeight(initialAspectRatio)

  if (!areAllowed) {
    return <Placeholder aspectRatio={initialAspectRatio} />
  }

  return (
    <iframe
      title={title}
      ref={ref}
      onLoad={onLoad}
      src={src}
      width={width}
      height={calculatedHeight}
      frameBorder={0}
      scrolling="no"
    />
  )
}

export default ArbitraryCodeEmbed
